import React from 'react';
import {
  ChipField,
  Loading,
  RecordContextProvider,
  SingleFieldList,
  useGetOne,
  useRecordContext,
} from 'react-admin';
import { Grid } from '@mui/material';

import auditTrackDiffGenerator, { AuditTrack } from '../audit/auditDiffGenerator/auditTrackDiffGenerator';
import AuditExtraInfo from '../audit/AuditExtraInfo';
import ChipStringField from '../../customFields/ChipStringField';
import NoResults from '../layout/NoResults';
import TextDiffField from '../utils/auditTracks/TextDiffField';
import ArrayDiffField from '../utils/auditTracks/ArrayDiffField';
import DateDiffField from '../utils/auditTracks/DateDiffField';
import SelectDiffField from '../utils/auditTracks/SelectDiffField';
import StatusDiffField from '../utils/auditTracks/StatusDiffField';
import ReferenceArrayDiffField from '../utils/auditTracks/ReferenceArrayDiffField';
import { productTypesChoices } from '../../constants/productTypes';
import resourceNames from '../../constants/resourceNames';
import { IProduct } from '../../utilities/schemas/product';
import { ProductPartiesDataGrid } from '../../customFields/ProductParties';

const ProductAuditDetails = () => {
  const record = useRecordContext<AuditTrack<IProduct>>();
  const { data, isLoading } = useGetOne<AuditTrack<IProduct>>(
    `audit-tracks/${record?.id}/previous`,
    { id: '' },
    { enabled: !!record },
  );

  if (!record) return <NoResults />;

  if (!data) {
    if (isLoading) return <Loading />;
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const isElementCreated = data.id === record.id;

  if (!record.documentAfter || (!isElementCreated && !data.documentAfter)) {
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const auditTracksDiff = auditTrackDiffGenerator<IProduct>({
    current: record,
    previous: !isElementCreated ? data : undefined,
  });

  const { resourceData } = auditTracksDiff;

  const hasFieldChanges = {
    id: resourceData.id?.hasChanged,
    customIds: resourceData.customIds?.hasChanged,
    productNumber: resourceData.productNumber?.hasChanged,
    productDescription: resourceData.productDescription?.hasChanged,
    productType: resourceData.productType?.hasChanged,
    productInformation: resourceData.productInformation?.hasChanged,
    parties: resourceData.parties?.hasChanged,
    riskScore: resourceData.riskScore?.hasChanged,
    scoreLatestCalculationDate: resourceData.scoreLatestCalculationDate?.hasChanged,
    kycApprovedAt: resourceData.kycApprovedAt?.hasChanged,
    status: resourceData.status?.hasChanged,
    createdAt: resourceData.createdAt?.hasChanged,
    currencies: resourceData.currencies?.hasChanged,
    groups: resourceData.groups?.hasChanged,
    accountId: resourceData.accountId?.hasChanged,
  };

  const hasAnyChanges = Object.values(hasFieldChanges).some((field) => field);

  return (
    <Grid container spacing={6}>
      {!hasAnyChanges && <NoResults text="Only system changes detected" />}
      {hasAnyChanges && (
        <RecordContextProvider value={resourceData}>
          {hasFieldChanges.id && <TextDiffField source="id" label="ID" />}

          {hasFieldChanges.accountId && <TextDiffField source="accountId" label="Account ID" />}

          {hasFieldChanges.customIds && (
            <ArrayDiffField source="customIds" label="Custom IDs">
              <SingleFieldList linkType={false}>
                <ChipStringField />
              </SingleFieldList>
            </ArrayDiffField>
          )}

          {hasFieldChanges.productNumber && (
            <TextDiffField source="productNumber" label="Product number" />
          )}

          {hasFieldChanges.productDescription && (
            <TextDiffField source="productDescription" label="Product description" />
          )}

          {hasFieldChanges.productType && (
            <SelectDiffField source="productType" label="Product type" choices={productTypesChoices} />
          )}

          {hasFieldChanges.productInformation && (
            <TextDiffField source="productInformation" label="Product information" />
          )}

          {hasFieldChanges.riskScore && <TextDiffField source="riskScore" label="Risk score" />}

          {hasFieldChanges.scoreLatestCalculationDate && (
            <DateDiffField source="scoreLatestCalculationDate" label="Score latest calculation date" />
          )}

          {hasFieldChanges.kycApprovedAt && (
            <DateDiffField source="kycApprovedAt" label="KYC approved at" />
          )}

          {hasFieldChanges.status && <StatusDiffField source="status" label="Status" />}

          {hasFieldChanges.createdAt && <DateDiffField source="createdAt" label="Created at" />}

          {hasFieldChanges.currencies && (
            <ArrayDiffField source="currencies" label="Currencies">
              <SingleFieldList linkType={false}>
                <ChipStringField />
              </SingleFieldList>
            </ArrayDiffField>
          )}

          {hasFieldChanges.groups && (
            <ReferenceArrayDiffField
              source="groups"
              label="Groups"
              reference={resourceNames.GROUPS}
            >
              <SingleFieldList linkType={false}>
                <ChipField source="name" size="small" />
              </SingleFieldList>
            </ReferenceArrayDiffField>
          )}

          {hasFieldChanges.parties && (
            <ArrayDiffField width="100%" source="parties" label="Parties">
              <ProductPartiesDataGrid />
            </ArrayDiffField>
          )}
        </RecordContextProvider>
      )}
      {record.extraInfo && <AuditExtraInfo />}
    </Grid>
  );
};

export default ProductAuditDetails;
